import { driver, DriveStep, Config } from "driver.js";
import "driver.js/dist/driver.css";
import "./onboardingTour.css";
import { TFunction } from "i18next";

type QueuedTour = {
  key?: string;
  steps: DriveStep[];
  overrides?: Partial<Config>;
};

// Only one driver.js tour should ever be visible at a time. Any tour
// requested while another is active is queued and started once the active
// one is closed/completed, instead of overlapping it.
let isTourActive = false;
const tourQueue: QueuedTour[] = [];
let activeTourKey: string | null = null;
const queuedTourKeys = new Set<string>();

const startNextQueuedTour = (): void => {
  const next = tourQueue.shift();
  if (!next) {
    isTourActive = false;
    activeTourKey = null;
    return;
  }

  isTourActive = true;
  const { key, overrides, steps } = next;
  activeTourKey = key ?? null;
  if (key) {
    queuedTourKeys.delete(key);
  }
  const tour = driver({
    showProgress: true,
    allowClose: true,
    overlayOpacity: 0.6,
    smoothScroll: true,
    steps,
    ...overrides,
    onDestroyed: (element, step, opts) => {
      activeTourKey = null;
      overrides?.onDestroyed?.(element, step, opts);
      startNextQueuedTour();
    },
  });

  tour.drive();
};

/**
 * Runs a driver.js tour, silently skipping steps whose target element is
 * missing or not currently visible (e.g. desktop-only / mobile-only nodes).
 * If another tour is already running, this one is queued so tours never
 * overlap on screen.
 */
export const runOnboardingTour = (
  steps: DriveStep[],
  t: TFunction,
  overrides?: Partial<Config>,
  tourKey?: string,
): void => {
  const visibleSteps = steps.filter((step) => {
    if (typeof step.element !== "string") return true;
    const el = document.querySelector(step.element) as HTMLElement | null;
    return Boolean(el && el.offsetParent !== null);
  });

  if (visibleSteps.length === 0) return;

  if (tourKey) {
    if (activeTourKey === tourKey || queuedTourKeys.has(tourKey)) {
      return;
    }
    queuedTourKeys.add(tourKey);
  }

  tourQueue.push({
    key: tourKey,
    steps: visibleSteps,
    overrides: {
      nextBtnText: String(t("onboarding.controls.next")),
      prevBtnText: String(t("onboarding.controls.previous")),
      doneBtnText: String(t("onboarding.controls.done")),
      progressText: String(t("onboarding.controls.progress")),
      ...overrides,
    },
  });

  if (!isTourActive) {
    startNextQueuedTour();
  }
};

export const buildLayoutTourSteps = (t: TFunction): DriveStep[] => [
  {
    element: "#onboarding-sidebar-menu",
    popover: {
      title: String(t("onboarding.layout.sidebar.title")),
      description: String(t("onboarding.layout.sidebar.description")),
      side: "right",
    },
  },
  {
    element: "#onboarding-mobile-menu",
    popover: {
      title: String(t("onboarding.layout.mobileMenu.title")),
      description: String(t("onboarding.layout.mobileMenu.description")),
      side: "bottom",
    },
  },
  {
    element: "#onboarding-theme-toggle",
    popover: {
      title: String(t("onboarding.layout.theme.title")),
      description: String(t("onboarding.layout.theme.description")),
      side: "bottom",
    },
  },
  {
    element: "#onboarding-language-menu",
    popover: {
      title: String(t("onboarding.layout.language.title")),
      description: String(t("onboarding.layout.language.description")),
      side: "bottom",
    },
  },
  {
    element: "#onboarding-profile-menu",
    popover: {
      title: String(t("onboarding.layout.profile.title")),
      description: String(t("onboarding.layout.profile.description")),
      side: "bottom",
    },
  },
  {
    element: "#onboarding-shortcut-dashboard",
    popover: {
      title: String(t("onboarding.layout.shortcutDashboard.title")),
      description: String(t("onboarding.layout.shortcutDashboard.description")),
      side: "top",
    },
  },
  {
    element: "#onboarding-shortcut-restart",
    popover: {
      title: String(t("onboarding.layout.shortcutRestart.title")),
      description: String(t("onboarding.layout.shortcutRestart.description")),
      side: "top",
    },
  },
  {
    element: "#onboarding-shortcut-logout",
    popover: {
      title: String(t("onboarding.layout.shortcutLogout.title")),
      description: String(t("onboarding.layout.shortcutLogout.description")),
      side: "top",
    },
  },
];

export const buildTeachingThemeTourSteps = (t: TFunction): DriveStep[] => [
  {
    element: "#onboarding-teaching-theme-select",
    popover: {
      title: String(t("onboarding.teachingThemeFilter.title")),
      description: String(t("onboarding.teachingThemeFilter.description")),
      side: "bottom",
    },
  },
];

export const buildAudioCardTourSteps = (t: TFunction): DriveStep[] => [
  {
    element: "#onboarding-audio-main",
    popover: {
      title: String(t("onboarding.audioCard.main.title")),
      description: String(t("onboarding.audioCard.main.description")),
      side: "right",
    },
  },
  {
    element: "#onboarding-audio-save",
    popover: {
      title: String(t("onboarding.audioCard.save.title")),
      description: String(t("onboarding.audioCard.save.description")),
      side: "top",
    },
  },
  {
    element: "#onboarding-audio-share",
    popover: {
      title: String(t("onboarding.audioCard.share.title")),
      description: String(t("onboarding.audioCard.share.description")),
      side: "top",
    },
  },
  {
    element: "#onboarding-audio-summary",
    popover: {
      title: String(t("onboarding.audioCard.summary.title")),
      description: String(t("onboarding.audioCard.summary.description")),
      side: "top",
    },
  },
];
